import React from 'react';
import { motion } from 'framer-motion';
import {
    benefits,
    staggerCards,
    buttonPulse,
    floatingAnimation
} from '../data/constants';
import AnimatedSection from './AnimatedSection';
import ParallaxElement from './ParallaxElement';

const Features: React.FC = () => {
    const iconMap: { [key: string]: JSX.Element } = {
        GraduationCap: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                whileHover={{ rotate: 360, scale: 1.2 }}
                transition={{ duration: 0.6 }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </motion.svg>
        ),
        Heart: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, 0]
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                whileHover={{ scale: 1.3, fill: "currentColor" }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </motion.svg>
        ),
        Users: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                whileHover={{
                    scale: 1.2,
                    rotate: [0, -10, 10, 0]
                }}
                transition={{ duration: 0.5 }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </motion.svg>
        ),
        BarChart3: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                whileHover={{
                    scale: 1.2,
                    y: [-2, -4, -2]
                }}
                transition={{ duration: 0.4 }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </motion.svg>
        ),
        Globe: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ rotate: [0, 360] }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "linear"
                }}
                whileHover={{ scale: 1.3 }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </motion.svg>
        ),
        Award: (
            <motion.svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                whileHover={{
                    scale: 1.2,
                    rotate: [0, 15, -15, 0],
                    fill: "currentColor"
                }}
                transition={{ duration: 0.6 }}
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </motion.svg>
        )
    };

    return (
        <section id="features" className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
            {/* Background decorative elements */}
            <div className="absolute inset-0 z-0">
                <ParallaxElement speed={0.3} direction="up">
                    <motion.div
                        animate={floatingAnimation}
                        className="absolute top-20 left-10 text-brand-gold text-6xl opacity-5"
                    >
                        ✨
                    </motion.div>
                </ParallaxElement>

                <ParallaxElement speed={0.5} direction="down">
                    <motion.div
                        animate={{
                            rotate: [0, 360],
                            scale: [1, 1.2, 1],
                        }}
                        transition={{
                            duration: 15,
                            repeat: Infinity,
                            ease: "linear"
                        }}
                        className="absolute top-1/4 right-20 w-24 h-24 border-2 border-brand-dark-red/10 rounded-full"
                    />
                </ParallaxElement>

                <ParallaxElement speed={0.4} direction="up">
                    <motion.div
                        animate={{
                            y: [0, -20, 0],
                            rotate: [0, 10, 0],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        className="absolute bottom-20 left-1/4 text-brand-dark-red text-4xl opacity-10"
                    >
                        🎵
                    </motion.div>
                </ParallaxElement>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <AnimatedSection animation="fadeInUp" className="text-center mb-16">
                    <motion.h2
                        className="font-cinzel text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        Why Choose <motion.span
                            className="text-brand-dark-red"
                            animate={{
                                textShadow: [
                                    "0 0 0px rgba(139, 0, 0, 0)",
                                    "0 0 20px rgba(139, 0, 0, 0.3)",
                                    "0 0 0px rgba(139, 0, 0, 0)"
                                ]
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            SwarSadhana
                        </motion.span>?
                    </motion.h2>
                    <motion.p
                        className="font-poppins text-xl text-gray-600 max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        Experience the perfect blend of traditional Indian arts and modern learning technology
                    </motion.p>
                </AnimatedSection>

                <AnimatedSection animation="bounceIn" delay={0.4}>
                    <motion.div
                        variants={staggerCards}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        {benefits.map((benefit, index) => (
                            <motion.div
                                key={benefit.id}
                                variants={{
                                    hidden: { opacity: 0, y: 50, scale: 0.9 },
                                    visible: {
                                        opacity: 1,
                                        y: 0,
                                        scale: 1,
                                        transition: {
                                            type: "spring",
                                            stiffness: 100,
                                            damping: 15,
                                            delay: index * 0.1
                                        }
                                    }
                                }}
                                whileHover={{
                                    scale: 1.05,
                                    y: -10,
                                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                                    rotateY: 5
                                }}
                                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 relative overflow-hidden group"
                            >
                                {/* Animated background gradient on hover */}
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-br from-brand-dark-red/5 to-brand-gold/5 opacity-0"
                                    whileHover={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                />

                                {/* Floating particles on hover */}
                                <motion.div
                                    className="absolute top-4 right-4 w-2 h-2 bg-brand-gold rounded-full opacity-0"
                                    whileHover={{
                                        opacity: [0, 1, 0],
                                        y: [-10, -30, -50],
                                        x: [0, 10, -10]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                />

                                <motion.div
                                    className="flex items-center justify-center w-16 h-16 bg-brand-dark-red rounded-full text-white mb-6 mx-auto relative z-10"
                                    whileHover={{
                                        scale: 1.2,
                                        rotate: 360,
                                        backgroundColor: "#FFD700"
                                    }}
                                    transition={{
                                        duration: 0.6,
                                        backgroundColor: { duration: 0.3 }
                                    }}
                                >
                                    {iconMap[benefit.icon]}

                                    {/* Ripple effect */}
                                    <motion.div
                                        className="absolute inset-0 rounded-full border-2 border-brand-dark-red"
                                        initial={{ scale: 1, opacity: 1 }}
                                        whileHover={{
                                            scale: 1.5,
                                            opacity: 0,
                                            borderColor: "#FFD700"
                                        }}
                                        transition={{ duration: 0.6 }}
                                    />
                                </motion.div>

                                <motion.h3
                                    className="font-cinzel text-xl font-bold text-gray-900 mb-4 text-center relative z-10"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    {benefit.title}
                                </motion.h3>

                                <motion.p
                                    className="font-poppins text-gray-600 text-center leading-relaxed relative z-10"
                                    whileHover={{ color: "#374151" }}
                                >
                                    {benefit.description}
                                </motion.p>

                                {/* Enhanced decorative element */}
                                <motion.div
                                    className="mt-6 flex justify-center relative z-10"
                                    whileHover={{ scale: 1.2 }}
                                >
                                    <motion.div
                                        className="w-12 h-1 bg-brand-gold rounded-full"
                                        whileHover={{
                                            width: "3rem",
                                            backgroundColor: "#8B0000"
                                        }}
                                        transition={{ duration: 0.3 }}
                                    />
                                </motion.div>

                                {/* Corner decoration */}
                                <motion.div
                                    className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-brand-gold/10 to-transparent rounded-bl-full opacity-0"
                                    whileHover={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                />
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatedSection>

                {/* Enhanced Call to Action */}
                <AnimatedSection animation="rotateIn" delay={0.8} className="text-center mt-16">
                    <motion.div
                        className="bg-brand-dark-red p-1 rounded-2xl inline-block relative overflow-hidden"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Animated border */}
                        <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-brand-gold via-brand-dark-red to-brand-gold rounded-2xl"
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        />

                        <div className="bg-white rounded-2xl px-8 py-6 relative z-10">
                            <motion.h3
                                className="font-cinzel text-2xl font-bold text-gray-900 mb-4"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                Ready to Begin Your Journey?
                            </motion.h3>
                            <motion.p
                                className="font-poppins text-gray-600 mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Join thousands of students who have discovered their passion through SwarSadhana
                            </motion.p>
                            <motion.button
                                whileHover={{
                                    scale: 1.05,
                                    boxShadow: "0 10px 30px rgba(255, 215, 0, 0.4)",
                                    y: -2
                                }}
                                whileTap={{ scale: 0.95 }}
                                animate={buttonPulse}
                                onClick={() => {
                                    const element = document.getElementById('courses');
                                    element?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="bg-brand-gold text-gray-900 px-6 py-3 rounded-lg font-poppins font-semibold text-base shadow-lg hover:bg-yellow-500 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                            >
                                <motion.span
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                    initial={{ x: "-100%" }}
                                    whileHover={{ x: "100%" }}
                                    transition={{ duration: 0.6 }}
                                />
                                <span className="relative z-10">Explore Our Courses</span>
                            </motion.button>
                        </div>
                    </motion.div>
                </AnimatedSection>
            </div>
        </section>
    );
};

export default Features; 
